/**
 * SEC-LOCKDOWN-1A S3 / D1 — single classifier for job_outputs.url shapes.
 *
 * Used by BOTH server (api/) and client so they cannot diverge.
 * Pure string logic only — no Supabase / fetch imports.
 *
 * Shapes:
 *  (a) footwear-outputs public URL  → sign (extract path)
 *  (b) bare footwear-outputs path   → sign
 *  (c) already-signed footwear-outputs URL → re-sign via extracted path
 *      (or treat as passthrough display URL if preferred by caller)
 *  (d) external URL (fal.ai, placeholders, Meshy, …) → passthrough
 *  (e) null / empty → empty
 */

export const OUTPUT_BUCKET_NAME = "footwear-outputs";

export type OutputRefClass =
  | {
      kind: "sign";
      /** Object path inside footwear-outputs (no bucket prefix). */
      path: string;
      source: "public_url" | "signed_url" | "bare_path";
    }
  | {
      kind: "passthrough";
      /** Absolute http(s) URL — do not sign. */
      url: string;
    }
  | { kind: "empty" };

const PUBLIC_PATH_RE =
  /\/storage\/v1\/object\/public\/footwear-outputs\/(.+)$/i;
const SIGN_PATH_RE =
  /\/storage\/v1\/object\/(?:sign|authenticated)\/footwear-outputs\/(.+)$/i;

function stripBucketPrefix(path: string): string {
  let p = path.replace(/^\/+/, "");
  if (p.startsWith("public/")) p = p.slice(7);
  const prefix = `${OUTPUT_BUCKET_NAME}/`;
  if (p.toLowerCase().startsWith(prefix.toLowerCase())) {
    p = p.slice(prefix.length);
  }
  return p;
}

/**
 * Classify a stored job_outputs.url (or thumb_url) value.
 * This is the ONE shared function for server + client resolvers.
 */
export function classifyOutputRef(
  stored: string | null | undefined,
): OutputRefClass {
  if (stored == null) return { kind: "empty" };
  if (typeof stored !== "string") return { kind: "empty" };
  const raw = stored.trim();
  if (!raw) return { kind: "empty" };

  if (!/^https?:\/\//i.test(raw)) {
    const path = stripBucketPrefix(raw);
    if (!path || !path.includes("/")) {
      // Reject garbage single-segment tokens — treat as empty
      return { kind: "empty" };
    }
    return { kind: "sign", path, source: "bare_path" };
  }

  try {
    const u = new URL(raw);
    const pub = u.pathname.match(PUBLIC_PATH_RE);
    if (pub?.[1]) {
      return {
        kind: "sign",
        path: decodeURIComponent(pub[1]),
        source: "public_url",
      };
    }
    const signed = u.pathname.match(SIGN_PATH_RE);
    if (signed?.[1]) {
      return {
        kind: "sign",
        path: decodeURIComponent(signed[1]),
        source: "signed_url",
      };
    }
  } catch {
    return { kind: "empty" };
  }

  // Any other http(s) host (fal.ai debug.jpg, /shrt/, placeholders, Meshy, …)
  return { kind: "passthrough", url: raw };
}

/**
 * Extract footwear-outputs object path when the ref must be signed; else null.
 * Prefer classifyOutputRef for full branching.
 */
export function parseOutputStoragePath(
  stored: string | null | undefined,
): string | null {
  const c = classifyOutputRef(stored);
  return c.kind === "sign" ? c.path : null;
}

export function isHttpUrl(value: string): boolean {
  return /^https?:\/\//i.test(value.trim());
}

/** True only when `value` parses as an absolute http(s) URL. */
export function isAbsoluteHttpUrl(value: string): boolean {
  try {
    const u = new URL(value.trim());
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Fail fast before a vendor call when `imageUrl` is a storage path / garbage.
 * Names the job origin so "Invalid URL" from `new URL()` never ships alone.
 */
export function assertAbsoluteHttpImageUrl(
  imageUrl: string,
  context: { origin: string; field?: string },
): void {
  const field = context.field ?? "image_url";
  const raw = typeof imageUrl === "string" ? imageUrl : String(imageUrl ?? "");
  if (!isAbsoluteHttpUrl(raw)) {
    throw new Error(
      `[vendor-image] origin=${context.origin} ${field} is not an absolute http(s) URL (len=${raw.length}): ${JSON.stringify(raw.slice(0, 160))}`,
    );
  }
}

/**
 * Cache-bust without corrupting signed URLs that already have ?token=.
 * Returns the URL unchanged when it is not a valid absolute URL.
 */
export function withOutputCacheBust(url: string): string {
  try {
    const u = new URL(url);
    u.searchParams.set("_t", String(Date.now()));
    return u.toString();
  } catch {
    return url;
  }
}
